import type { ReactElement } from 'react'
import type { TFunction } from 'i18next'
import { Column } from 'primereact/column'
import type { LocalizedText } from '../../../lib/localizedText'
import { InputSwitch } from 'primereact/inputswitch'
import Button from '../../../components/ui/Button'
import Badge from '../../../components/ui/Badge'
import SortSpinner from '../../../components/ui/SortSpinner'
import type { CriteriaScore } from '../types'

interface ColumnOpts {
  t: TFunction
  /** Ad kolonu tek kolondur; değer seçili dilden gelir (`lib/localizedText`). */
  localized: LocalizedText
  busyId: number | null
  /** Görünen satır sayısı — son satırda "aşağı" pasif olması için. */
  rowCount: number
  /** Yeniden sıralama sürüyor mu — oklar kilitlenir. */
  reordering: boolean
  onMove: (row: CriteriaScore, dir: 'up' | 'down') => void
  onEdit: (row: CriteriaScore) => void
  onToggle: (row: CriteriaScore, active: boolean) => void
  onDelete: (row: CriteriaScore) => void
}

/**
 * Değerlendirme kriteri tablosunun sütunları (paylaşılan ui/DataTable'a beslenir). Baştaki **sıra** kolonu
 * yan yana ↑/↓ okları (ortak SortSpinner). TR/EN ad + **ağırlık (%)** + durum Badge + satır aksiyonları
 * (düzenle/sil). **Korumalı sistem kaydı yoktur.** (025 InvitationTypeColumns deseni + ağırlık sütunu.)
 */
export function criteriaScoreColumns({
  t,
  localized,
  busyId,
  rowCount,
  reordering,
  onMove,
  onEdit,
  onToggle,
  onDelete,
}: ColumnOpts): ReactElement[] {
  return [
    // Sıra kolonu — yalnızca oklar (SORT_INDEX değeri gösterilmez); liste daima sıra düzeninde.
    <Column
      key="sort"
      header={t('activity.criteriaScores.columns.sortIndex')}
      style={{ width: '6rem' }}
      body={(row: CriteriaScore, { rowIndex }) => (
        <SortSpinner
          onUp={() => onMove(row, 'up')}
          onDown={() => onMove(row, 'down')}
          disableUp={rowIndex === 0}
          disableDown={rowIndex === rowCount - 1}
          busy={reordering}
          labelUp={t('common.reorder.up')}
          labelDown={t('common.reorder.down')}
        />
      )}
    />,
    <Column
      key="name"
      header={t('activity.criteriaScores.columns.name')}
      body={(row: CriteriaScore) => localized(row.nameTr, row.nameEn)}
    />,
    <Column
      key="weight"
      header={t('activity.criteriaScores.columns.weight')}
      style={{ width: '8rem' }}
      body={(row: CriteriaScore) => `%${row.weight}`}
    />,
    <Column
      key="status"
      header={t('activity.criteriaScores.columns.status')}
      style={{ width: '8rem' }}
      body={(row: CriteriaScore) => {
        const active = row.recordStatus === 1
        return (
          <Badge tone={active ? 'green' : 'neutral'}>
            {active
              ? t('activity.criteriaScores.status.active')
              : t('activity.criteriaScores.status.passive')}
          </Badge>
        )
      }}
    />,
    <Column
      key="actions"
      header={t('activity.criteriaScores.columns.actions')}
      style={{ width: '10rem' }}
      body={(row: CriteriaScore) => {
        const active = row.recordStatus === 1
        return (
          <div className="admin-row-actions">
            <InputSwitch
              className="admin-switch"
              checked={active}
              disabled={busyId === row.id}
              onChange={(e) => onToggle(row, Boolean(e.value))}
              aria-label={row.nameTr}
              tooltip={t('activity.criteriaScores.actions.toggle')}
              tooltipOptions={{ position: 'bottom' }}
            />
            <Button
              icon="pi pi-pencil"
              rounded
              text
              aria-label={t('activity.criteriaScores.actions.edit')}
              tooltip={t('activity.criteriaScores.actions.edit')}
              tooltipOptions={{ position: 'bottom' }}
              onClick={() => onEdit(row)}
            />
            <Button
              icon="pi pi-trash"
              rounded
              text
              severity="danger"
              aria-label={t('activity.criteriaScores.actions.delete')}
              tooltip={t('activity.criteriaScores.actions.delete')}
              tooltipOptions={{ position: 'bottom' }}
              disabled={busyId === row.id}
              onClick={() => onDelete(row)}
            />
          </div>
        )
      }}
    />,
  ]
}
